import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Trash2, ArrowRightLeft } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Badge,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWaapRedirections,
  useCreateWaapRedirection,
  useUpdateWaapRedirection,
  useToggleWaapRedirection,
  useDeleteWaapRedirection,
  type WaapRedirectionRule,
  type WaapRedirectionInput,
} from '@/api/waap';
import { errMessage } from '@/pages/services/waap/waap-util';
import styles from './RedirectionTab.module.css';

// Match slot. The public Open-API redirection rule has exactly two fixed,
// optional match slots — URL path and query string — so the field selector is
// limited to these (vs the private free-form req.* selector family).
const FIELDS = [
  { value: 'url_path', label: 'URL Path' },
  { value: 'query_string', label: 'URL Query String' },
];

// Operator — the vendor MatchPattern set used by redirection/cache rules.
// Restricted to equals / prefix / suffix / regex (the public surface drops the
// private not*/i* variants).
const OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'prefix', label: 'starts with (prefix)' },
  { value: 'suffix', label: 'ends with (suffix)' },
  { value: 'regex', label: 'regex' },
];

const STATUS_CODES = [301, 302, 307, 308];

const opLabel = (op: string) => OPERATORS.find((o) => o.value === op)?.label ?? op;

const splitValues = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
const matchSummary = (r: WaapRedirectionRule) => {
  const parts: string[] = [];
  if (r.match_url_path)
    parts.push(`URL Path ${opLabel(r.match_url_path.operator)} [${r.match_url_path.patterns.join(', ')}]`);
  if (r.match_query_string)
    parts.push(`URL Query String ${opLabel(r.match_query_string.operator)} [${r.match_query_string.patterns.join(', ')}]`);
  return parts.join('; ') || '—';
};

// Local edit shape — one match condition keeps its patterns as raw text while
// typing (split only on save) so a typed comma isn't eaten by a round-trip.
interface MatchForm {
  what: string;
  operator: string;
  values: string;
}
interface FormState {
  name: string;
  priority: string;
  redirectionUrl: string;
  statusCode: number;
  matches: MatchForm[];
}

const NEW_MATCH: MatchForm = { what: 'url_path', operator: 'equals', values: '' };
const EMPTY_FORM: FormState = {
  name: '',
  priority: '',
  redirectionUrl: '',
  statusCode: 302,
  matches: [{ ...NEW_MATCH }],
};

function toForm(r: WaapRedirectionRule): FormState {
  const matches: MatchForm[] = [];
  if (r.match_url_path)
    matches.push({ what: 'url_path', operator: r.match_url_path.operator, values: r.match_url_path.patterns.join(', ') });
  if (r.match_query_string)
    matches.push({ what: 'query_string', operator: r.match_query_string.operator, values: r.match_query_string.patterns.join(', ') });
  return {
    name: r.name,
    priority: r.priority ? String(r.priority) : '',
    redirectionUrl: r.redirection_url,
    statusCode: r.status_code || 302,
    matches: matches.length ? matches : [{ ...NEW_MATCH }],
  };
}

/**
 * Redirection — a WAAP website-detail tab. Match a request (URL path and/or
 * query string) → redirect to a target URL + status. Backed by the PUBLIC
 * Open-API surface (/redirection). List + Create + Edit + Enable/Disable
 * + Delete are wired; enable/disable re-PUTs the full rule (no PATCH on the
 * public surface). Website fixed by the detail route (`:domainId`).
 */
export function RedirectionTab() {
  const { domainId } = useParams<{ domainId: string }>();
  const id = domainId ?? '';
  const { show } = useToast();

  const { data, isLoading } = useWaapRedirections(domainId);
  const rules = data ?? [];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WaapRedirectionRule | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [pendingDelete, setPendingDelete] = useState<WaapRedirectionRule | null>(null);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const setMatch = (i: number, patch: Partial<MatchForm>) =>
    set('matches', form.matches.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const addMatch = () => set('matches', [...form.matches, { ...NEW_MATCH }]);
  const removeMatch = (i: number) => set('matches', form.matches.filter((_, idx) => idx !== i));

  const closeForm = () => {
    setOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };
  const startAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };
  const startEdit = (r: WaapRedirectionRule) => {
    setEditing(r);
    setForm(toForm(r));
    setOpen(true);
  };

  const create = useCreateWaapRedirection(id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Redirection created' });
      closeForm();
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateWaapRedirection(id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Redirection updated' });
      closeForm();
    },
    onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }),
  });
  const toggle = useToggleWaapRedirection(id, {
    onSuccess: () => show({ tone: 'success', title: 'Redirection updated' }),
    onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }),
  });
  const remove = useDeleteWaapRedirection(id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Redirection deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const validMatches = form.matches.filter((m) => splitValues(m.values).length > 0);
  const valid = form.name.trim() && form.redirectionUrl.trim() && validMatches.length > 0;
  const saving = create.isPending || update.isPending;

  // The vendor rejects priority 0 ("Priority is empty or invalid"), so an empty
  // field defaults to max-existing + 1 (edits keep their current priority).
  const nextPriority = () => Math.max(0, ...(data ?? []).map((r) => r.priority ?? 0)) + 1;

  const submit = () => {
    // Fold the condition rows into the two fixed slots (last row wins per slot).
    const body: WaapRedirectionInput = {
      name: form.name.trim(),
      priority: Number(form.priority) > 0 ? Number(form.priority) : editing?.priority || nextPriority(),
      enabled: editing?.enabled ?? true,
      redirection_url: form.redirectionUrl.trim(),
      status_code: form.statusCode,
      match_url_path: null,
      match_query_string: null,
    };
    for (const m of validMatches) {
      const slot = { operator: m.operator, patterns: splitValues(m.values) };
      if (m.what === 'query_string') body.match_query_string = slot;
      else body.match_url_path = slot;
    }
    if (editing) update.mutate({ id: editing.id, body });
    else create.mutate(body);
  };

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  return (
    <Section
      title="Redirection"
      description="Match a request by URL path and/or query string, then redirect to a target URL. Lower priority evaluates first."
    >
      <Card>
        <CardHeader
          actions={
            <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={startAdd}>
              Add Rule
            </Button>
          }
        >
          <CardTitle>Redirection rules</CardTitle>
        </CardHeader>
        <CardBody className={styles.bodyGrid}>
          {rules.length === 0 ? (
            <EmptyState icon={<ArrowRightLeft size={20} />} title="No redirection rules yet" description="Add a rule to match a request path and redirect it to a target URL." />
          ) : (
            <div className={styles.rulesScroll}><div className={styles.rules}>
              {rules.map((r) => (
                <div key={r.id} className={styles.ruleRow}>
                  <Badge tone="neutral" appearance="subtle" size="sm">{r.priority}</Badge>
                  <span className={styles.name}>{r.name}</span>
                  <span className={styles.muted}>{matchSummary(r)}</span>
                  <span className={styles.muted} title={r.redirection_url}>
                    → {r.redirection_url}
                  </span>
                  <Badge tone={r.enabled ? 'success' : 'neutral'} appearance="subtle" size="sm">
                    {r.enabled ? `${r.status_code}` : 'Off'}
                  </Badge>
                  <div className={styles.actions}>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(r)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate({ rule: r, enabled: !r.enabled })}
                    >
                      {r.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setPendingDelete(r)}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div></div>
          )}

        </CardBody>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { if (!o) closeForm(); }}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Redirection Rule' : 'Add Redirection Rule'}</DialogTitle>
            <DialogDescription>
              Match a request by URL path and/or query string, then redirect to a target URL.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className={styles.dialogForm}>
              <div className={styles.grid3}>
                <Field label="Rule name" required>
                  {(p) => <Input {...p} value={form.name} placeholder="my-redirect" onChange={(e) => set('name', e.target.value)} />}
                </Field>
                <Field label="Priority" description="Lower evaluates first.">
                  {(p) => <Input {...p} type="number" value={form.priority} onChange={(e) => set('priority', e.target.value)} />}
                </Field>
                <Field label="Status code" description="301 permanent · 302 temporary · 307 temporary (preserves method) · 308 permanent (preserves method)">
                  {(p) => (
                    <Select value={String(form.statusCode)} onValueChange={(v) => set('statusCode', Number(v))}>
                      <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_CODES.map((c) => (
                          <SelectItem key={c} value={String(c)}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              </div>

              <Field label="Redirect to" required description="Target URL the matched request is redirected to.">
                {(p) => (
                  <Input
                    {...p}
                    placeholder="https://example.com/new-path"
                    value={form.redirectionUrl}
                    onChange={(e) => set('redirectionUrl', e.target.value)}
                  />
                )}
              </Field>

              <Field label="When incoming requests match" description="Field · operator · values (comma-separated). Operators: equals (exact), starts with (prefix), ends with (suffix), regex. All conditions must match.">
                {() => (
                  <div className={styles.matchList}>
                    {form.matches.map((m, i) => (
                      <div key={i} className={styles.matchRow}>
                        <Select value={m.what} onValueChange={(v) => setMatch(i, { what: v })}>
                          <SelectTrigger className={styles.fieldSelect}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FIELDS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Select value={m.operator} onValueChange={(v) => setMatch(i, { operator: v })}>
                          <SelectTrigger className={styles.opSelect}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {OPERATORS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input className={styles.grow} placeholder="/old-path, /legacy" value={m.values} onChange={(e) => setMatch(i, { values: e.target.value })} />
                        <IconButton
                          aria-label="Remove condition"
                          variant="ghost"
                          disabled={form.matches.length <= 1}
                          onClick={() => removeMatch(i)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </div>
                    ))}
                    <div>
                      <Button variant="ghost" size="sm" startIcon={<Plus size={14} />} onClick={addMatch}>
                        Add Condition
                      </Button>
                    </div>
                  </div>
                )}
              </Field>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button disabled={!valid || saving} onClick={submit}>
              {saving ? 'Saving…' : editing ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete redirection?"
        description={`Remove "${pendingDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Section>
  );
}
